"use client";

import { useCallback, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { RefreshCw, X } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { isCompanionView } from "@/lib/instance-path";
import { usePollWhileVisible } from "@/lib/use-visible-poll";

/**
 * "A new version is available — reload" notice for tabs that outlive a deploy.
 *
 * Polls /api/version (see app/api/version/route.ts) and compares against the
 * first value this page load observed — no build-time constant needs to reach
 * the client, and a tab opened mid-deploy self-corrects on its first poll.
 * Once a change is seen, polling stops and the notice stays until the user
 * reloads or dismisses it (dismissal lasts for the rest of the page load; the
 * reloaded page is by definition current).
 *
 * Polling pauses while the tab is hidden (browsers throttle the interval
 * anyway) and catches up via visibilitychange, so a laptop waking up learns
 * about last night's deploy immediately. Fetch failures are ignored: a failed
 * poll (offline, mid blue-green swap) must never surface an error for a
 * feature whose whole job is to be unobtrusive.
 */

const POLL_MS = 60_000;

export function UpdateNotice() {
  const { t } = useLingui();
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const companion = isCompanionView(usePathname());

  // Poll bookkeeping in refs, not effect-locals: the interval/visibility
  // scaffolding lives in usePollWhileVisible (shared with <MaintenanceHold/>),
  // so the check must survive as a stable callback.
  const baseline = useRef<string | null>(null);
  const done = useRef(false);

  const check = useCallback(async () => {
    if (done.current) return;
    try {
      const res = await fetch("/api/version", { cache: "no-store" });
      if (!res.ok) return;
      const body = (await res.json()) as { version?: unknown };
      if (typeof body.version !== "string" || body.version === "") return;
      if (baseline.current === null) {
        baseline.current = body.version;
      } else if (body.version !== baseline.current) {
        done.current = true;
        setUpdateReady(true);
      }
    } catch {
      // Offline or the server is mid-restart — try again next tick.
    }
  }, []);
  usePollWhileVisible(check, POLL_MS, { immediate: true });

  if (!updateReady || dismissed) return null;

  // The companion view (the chat the browser side panel frames) is narrow
  // and its message box is at the bottom, where the card goes: a slim line
  // across the top instead, and the reload stays one click away.
  if (companion) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed inset-x-0 top-0 z-50 flex items-center gap-2 border-b border-ink-mid bg-ink-soft px-3 py-1.5 text-cream"
      >
        <p className="flex-1 text-xs leading-snug text-stone-soft">
          <Trans>Squidler has been updated.</Trans>
        </p>
        <button
          onClick={() => { window.location.reload(); }}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-cream underline"
        >
          <RefreshCw className="size-3" />
          <Trans>Reload</Trans>
        </button>
        <button
          onClick={() => { setDismissed(true); }}
          aria-label={t`Dismiss update notice`}
          className="shrink-0 text-stone-soft transition hover:text-cream"
        >
          <X className="size-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-sm items-center gap-3 rounded-xl border border-ink-mid bg-ink-soft p-4 text-cream shadow-2xl sm:left-6 sm:right-auto sm:mx-0"
    >
      <p className="flex-1 text-sm leading-snug text-stone-soft">
        <Trans>Squidler has been updated. Reload to get the latest version.</Trans>
      </p>
      <button
        onClick={() => { window.location.reload(); }}
        className="inline-flex shrink-0 items-center gap-1.5 bg-signal px-3 py-1.5 text-sm font-medium text-cream transition hover:bg-signal-deep"
      >
        <RefreshCw className="size-3.5" />
        <Trans>Reload</Trans>
      </button>
      <button
        onClick={() => { setDismissed(true); }}
        aria-label={t`Dismiss update notice`}
        className="shrink-0 text-stone-soft transition hover:text-cream"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
